import { yupResolver } from '@hookform/resolvers/yup';
import { mealItemValidationSchema } from './mealItemValidation';
import { createMealItemAsync } from './createMealItemSlice';
import {
  FileWithPreview,
  MealItemFormData,
  toMealItemFormData,
} from '../../../app/data/models/mealItems/mealItemFormData';
import AppDropzone from '../../../app/components/AppDropzone';
import { useTranslation } from 'react-i18next';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { FieldErrors, Resolver, useForm } from 'react-hook-form';
import {
  Box,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Paper,
} from '@mui/material';
import AppTextInput from '../../../app/components/AppTextInput';
import { useEffect, useState } from 'react';
import FormButtons from '../../../app/components/FormButtons';
import AppSelectList from '../../../app/components/AppSelectList';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { fetchKitchenListAsync } from '../kitchens/kitchenListSlice';
import { MealType } from '../../../app/data/models/mealType';
import {
  fetchMealItemDetailsAsync,
  clearMealItemDetails,
} from './mealItemDetailsSlice';
import { updateMealItemAsync } from './updateMealItemSlice';
import { useMealType } from '../../../app/data/models/mealTypes';
import { ChevronRight } from '@mui/icons-material';
import MealItemLinkedCustomizations from './MealItemLinkedCustomizations';
import { LanguageFormTabs } from '../../../app/components/LanguageFormTabs';
import ErrorComponent from '../../../app/layout/ErrorComponent';
import { useRestaurant } from '../../../app/hooks/useRestaurant';
import {
  LanguageCode,
  LocalizedLanguageFormData,
} from '../../../app/data/models/common/localizedLanguage';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import { toMealItemFormModel } from '../../../app/data/models/mealItems/mealItemFormModel';
import { fetchTaxRatesAsync } from '../taxes/taxSlice';

interface Props {
  mealItemId?: string;
  restaurantId: string;
  cancelEdit: () => void;
}

const MealItemForm = ({ mealItemId, restaurantId, cancelEdit }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { status: createStatus } = useAppSelector(
    (state) => state.createMealItem
  );
  const { status: updateStatus } = useAppSelector(
    (state) => state.updateMealItem
  );
  const { mealItem: mealItemDetails, status: detailsStatus } = useAppSelector(
    (state) => state.mealItemDetails
  );
  const mealItem = mealItemDetails
    ? toMealItemFormData(mealItemDetails)
    : undefined;
  const { kitchens } = useAppSelector((state) => state.kitchens);
  const { taxRates } = useAppSelector((state) => state.taxes);
  const { restaurant, isLoading, error, retry } = useRestaurant(restaurantId);
  const [selectedKitchenId, setSelectedKitchenId] = useState<string>();
  const [showCustomizations, setShowCustomizations] = useState(false);

  // Track if form has been initialized
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const [activeTab, setActiveTab] = useState<number>(0);

  const defaultValues: MealItemFormData = {
    names: (restaurant?.languageCodes || []).reduce(
      (acc, code) => ({
        ...acc,
        [code]: '',
      }),
      {} as LocalizedLanguageFormData
    ),
    descriptions: (restaurant?.languageCodes || []).reduce(
      (acc, code) => ({
        ...acc,
        [code]: '',
      }),
      {} as LocalizedLanguageFormData
    ),
    price: 0,
    kitchenId: '',
    mealType: MealType.NonVeg,
    isAvailable: true,
    taxRateId: undefined,
  };

  const { control, handleSubmit, reset, watch, setValue, register } =
    useForm<MealItemFormData>({
      mode: 'onTouched',
      resolver: yupResolver(
        mealItemValidationSchema(
          t,
          (restaurant?.languageCodes || []) as LanguageCode[]
        )
      ) as unknown as Resolver<MealItemFormData>,
      defaultValues,
    });

  const watchFile = watch('picture') as FileWithPreview;
  const isLoadingForm =
    createStatus.includes('pending') || updateStatus.includes('pending');

  const header = (
    <ListTitleBox
      title={
        mealItemId
          ? t('meal-item.form.edit-title')
          : t('meal-item.form.create-title')
      }
      onBackClick={cancelEdit}
    />
  );

  // Fetch initial data
  useEffect(() => {
    dispatch(fetchKitchenListAsync(restaurantId));
    dispatch(fetchTaxRatesAsync({ restaurantId }));

    if (mealItemId) {
      dispatch(fetchMealItemDetailsAsync({ restaurantId, mealItemId }));
    }

    return () => {
      dispatch(clearMealItemDetails());
    };
  }, [mealItemId, restaurantId, dispatch]);

  // Form initialization for edit mode
  useEffect(() => {
    if (mealItem && !isFormInitialized) {
      reset({
        names: mealItem.names,
        descriptions: mealItem.descriptions,
        price: mealItem.price,
        kitchenId: mealItem.kitchenId,
        mealType: mealItem.mealType,
        isAvailable: mealItem.isAvailable,
        picture: mealItem.picture,
        taxRateId: mealItem.taxRateId,
      });
      setSelectedKitchenId(mealItem.kitchenId);
      setIsFormInitialized(true);
    }
  }, [mealItem, reset]);

  // Form initialization for create mode
  useEffect(() => {
    if (!mealItemId && restaurant?.languageCodes && !isFormInitialized) {
      reset(defaultValues);
      setIsFormInitialized(true);
    }
  }, [restaurant?.languageCodes, mealItemId]);

  // Handle default kitchen selection for new items
  useEffect(() => {
    if (!mealItemId && kitchens && kitchens.length > 0 && !selectedKitchenId) {
      setSelectedKitchenId(kitchens[0].id);
      setValue('kitchenId', kitchens[0].id);
    }
  }, [kitchens, selectedKitchenId, mealItemId, setValue]);

  // Handle form submission
  const handleFormSubmit = async (data: MealItemFormData) => {
    try {
      // Ensure taxRateId is properly handled (convert empty string to undefined)
      const formData = {
        ...data,
        taxRateId: data.taxRateId || undefined,
      };

      if (mealItemId) {
        await dispatch(
          updateMealItemAsync({
            restaurantId,
            mealItemId,
            data: toMealItemFormModel(formData),
          })
        ).unwrap();
      } else {
        await dispatch(
          createMealItemAsync({
            restaurantId,
            data: toMealItemFormModel(formData),
          })
        ).unwrap();
      }
      cancelEdit();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const onError = (errors: FieldErrors<MealItemFormData>) => {
    if (!restaurant?.languageCodes?.length) return;

    // Check names field errors
    if (errors.names) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => errors.names && errors.names[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
        return;
      }
    }

    // Check descriptions field errors
    if (errors.descriptions) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => errors.descriptions && errors.descriptions[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  const mealTypeOptions = Object.values(MealType)
    .filter((value) => typeof value === 'number')
    .map((value) => ({
      id: value as number,
      name: useMealType(value as MealType).name,
    }));

  if (isLoading) return <LoadingComponent />;
  if (error) return <ErrorComponent onPrimaryAction={retry} />;
  if (!restaurant) return null;

  if (detailsStatus === 'pendingFetchDetails') {
    return <LoadingComponent message={t('common.loading')} />;
  }

  if (showCustomizations) {
    return (
      <MealItemLinkedCustomizations
        restaurantId={restaurantId}
        mealItemId={mealItemId!}
        onBack={() => setShowCustomizations(false)}
      />
    );
  }

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(handleFormSubmit, onError)} noValidate>
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ mb: { xs: 2, sm: 3 } }}
          >
            <Grid item xs={12}>
              <LanguageFormTabs
                languageCodes={restaurant.languageCodes}
                control={control}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fields={[
                  {
                    name: 'names',
                    label: t('meal-item.form.fields.name'),
                  },
                  {
                    name: 'descriptions',
                    label: t('meal-item.form.fields.description'),
                    multiline: true,
                    rows: 3,
                  },
                ]}
              />
            </Grid>

            {/* Common fields */}
            <Grid item xs={12}>
              <AppTextInput
                control={control}
                name="price"
                label={t('meal-item.form.fields.price')}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AppSelectList
                control={control}
                items={mealTypeOptions}
                name="mealType"
                label={t('meal-item.form.fields.type')}
                defaultValue={mealItem?.mealType ?? mealTypeOptions[0]?.id}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>{t('meal-item.form.fields.kitchen')}</InputLabel>
              {selectedKitchenId && (
                <Select
                  {...register('kitchenId')}
                  value={selectedKitchenId}
                  onChange={(event) =>
                    setSelectedKitchenId(event.target.value as string)
                  }
                  disabled={kitchens.length === 0}
                  fullWidth
                >
                  {kitchens.map((kitchen) => (
                    <MenuItem key={kitchen.id} value={kitchen.id}>
                      {kitchen.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Grid>

            <Grid item xs={12}>
              <InputLabel>{t('meal-item.form.fields.tax-rate')}</InputLabel>
              <Select
                {...register('taxRateId')}
                value={watch('taxRateId') ?? ''}
                onChange={(event) =>
                  setValue(
                    'taxRateId',
                    (event.target.value as string) || undefined
                  )
                }
                fullWidth
              >
                {taxRates.map((tr) => (
                  <MenuItem key={tr.id} value={tr.id}>
                    {`${tr.name} (${tr.rate.toFixed(2)}%) ${tr.isDefault ? t('common.default') : ''}`}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('isAvailable') || false}
                    onChange={(e) => setValue('isAvailable', e.target.checked)}
                  />
                }
                label={t('meal-item.form.fields.is-available')}
              />
            </Grid>

            <Grid item xs={12}>
              <AppDropzone
                control={control}
                name="picture"
                label={t('common.upload-photo')}
                maxWidth={300}
                maxHeight={300}
                aspectRatio={1}
              />
              {(watchFile?.preview || mealItem?.pictureUrl) && (
                <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
                  <img
                    src={watchFile?.preview || mealItem?.pictureUrl}
                    alt={t('meal-item.form.fields.image-preview')}
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          <FormButtons
            loading={isLoadingForm}
            submitText={mealItemId ? t('common.update') : t('common.create')}
          />
        </form>
        <Divider sx={{ my: 3 }} />

        {mealItemId && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
              onClick={() => setShowCustomizations(true)}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>
                  {t('meal-item.form.manage-customizations')}
                </Typography>
                <ChevronRight />
              </Box>
            </Box>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default MealItemForm;
